"use client";

import { useState, useEffect, useCallback } from 'react';
import { TableService } from '../services/table-service';

interface UseTablesResult {
  tables: any[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for managing lakehouse tables
 * @param orgId - Organization ID
 * @param namespace - Optional namespace filter
 * @returns Table management state and operations
 */
export function useTables(orgId: string, namespace?: string): UseTablesResult {
  const [tables, setTables] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTables = useCallback(async () => {
    if (!orgId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await TableService.getTables(orgId, namespace);
      setTables(data.tables || data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tables');
    } finally {
      setLoading(false);
    }
  }, [orgId, namespace]);

  const refetch = useCallback(async () => {
    await fetchTables();
  }, [fetchTables]);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  return {
    tables,
    loading,
    error,
    refetch,
  };
}

interface UseTableDetailsResult {
  table: any | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for getting table details
 * @param tableId - Table ID
 * @param orgId - Organization ID
 * @returns Table details state
 */
export function useTableDetails(tableId: string | null, orgId: string): UseTableDetailsResult {
  const [table, setTable] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTableDetails = useCallback(async () => {
    if (!tableId || !orgId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await TableService.getTableDetails(tableId, orgId);
      setTable(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch table details');
    } finally {
      setLoading(false);
    }
  }, [tableId, orgId]);

  const refetch = useCallback(async () => {
    await fetchTableDetails();
  }, [fetchTableDetails]);

  useEffect(() => {
    fetchTableDetails();
  }, [fetchTableDetails]);

  return {
    table,
    loading,
    error,
    refetch,
  };
}

interface UseTableDataResult {
  data: any | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for getting table data preview
 * @param tableId - Table ID
 * @param orgId - Organization ID
 * @param limit - Row limit for preview
 * @returns Table data state
 */
export function useTableData(tableId: string | null, orgId: string, limit: number = 100): UseTableDataResult {
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTableData = useCallback(async () => {
    if (!tableId || !orgId) return;
    
    try {
      setLoading(true);
      setError(null);
      const result = await TableService.getTablePreview(tableId, orgId, limit);
      console.log('Table data result:', result);
      console.log('Result type:', typeof result);
      console.log('Has rows?', !!result?.rows);
      console.log('Rows length:', result?.rows?.length);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch table data');
    } finally {
      setLoading(false);
    }
  }, [tableId, orgId, limit]);

  const refetch = useCallback(async () => {
    await fetchTableData();
  }, [fetchTableData]);

  useEffect(() => {
    fetchTableData();
  }, [fetchTableData]);

  return {
    data,
    loading,
    error,
    refetch,
  };
}